import React, { useState } from 'react';
import { Table, Alert, Input } from 'reactstrap';
import { displayValue } from '../../../../TenderOpening/utils/tenderOpeningHelpers';
import notify, { MESSAGES } from '../../../../../utils/notify';
import {
  updateLpgAlphaHps,
  updateLpgDetailAlphaHps,
  resolveLpgAlphaTarget,
} from './utils';
import { resolveInformationPrompt } from '../../../utils/informationPrompt';
import './ParcelIncotermTableLpg.css';

const FOB_LOCATION_DISPLAY_LIMIT = 3;

const parseAlphaInput = value => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const CargoRatioEmptyState = () => (
  <Alert
    color='warning'
    className='d-flex flex-column align-items-center justify-content-center text-center w-100 h-100 mb-0 py-4 px-2 border-0 rounded-0'
  >
    <i className='ri-error-warning-line fs-24 mb-1'></i>
    <span className='fs-13 fw-bold'>
      Please select cargo ratio above to continue
    </span>
  </Alert>
);

const FobPortLabel = ({
  port,
  portNames = null,
  limit = FOB_LOCATION_DISPLAY_LIMIT,
}) => {
  const [expanded, setExpanded] = useState(false);
  const names = (Array.isArray(portNames) ? portNames : []).filter(Boolean);

  if (!names.length) {
    return <>{displayValue(port)}</>;
  }

  const visible =
    expanded || names.length <= limit ? names : names.slice(0, limit);

  return (
    <>
      {visible.join(', ')}
      {names.length > limit ? (
        <>
          {' '}
          <button
            type='button'
            className='btn btn-link btn-sm p-0 align-baseline text-primary fw-medium'
            style={{ fontSize: 'inherit' }}
            onClick={() => setExpanded(prev => !prev)}
          >
            {expanded ? 'Hide' : 'Show all'}
          </button>
        </>
      ) : null}
    </>
  );
};

const PortCell = ({
  port,
  portNames = null,
  aldAdd = null,
  parcelSizes = [],
  showParcelSizes = false,
}) => {
  const names = (Array.isArray(portNames) ? portNames : []).filter(Boolean);
  const portText = String(port ?? '').trim();
  const hasPort = (portText !== '' && portText !== '-') || names.length > 0;
  const aldAddText = String(aldAdd ?? '').trim();
  const hasAldAdd = aldAddText !== '' && aldAddText !== '-';

  const sizes = (Array.isArray(parcelSizes) ? parcelSizes : []).filter(size => {
    const text = String(size ?? '').trim();
    return text && text !== '-' && !text.startsWith('-');
  });

  if (!hasPort && !hasAldAdd && !(showParcelSizes && sizes.length)) {
    return <span className='text-muted fs-13'>-</span>;
  }

  return (
    <div className='lpg-port-cell'>
      <div className='lpg-port-cell-block'>
        <span className='lpg-port-cell-label'>Location</span>
        <span className='lpg-port-cell-value'>
          {hasPort ? (
            <FobPortLabel port={port} portNames={names} />
          ) : (
            <span className='text-muted'>-</span>
          )}
        </span>
      </div>

      {showParcelSizes ? (
        <div className='lpg-port-cell-block'>
          <span className='lpg-port-cell-label'>Parcel Size</span>
          <div className='lpg-port-cell-sizes'>
            {sizes.length ? (
              sizes.map((size, index) => (
                <span key={`${size}-${index}`} className='lpg-port-cell-value'>
                  {size}
                </span>
              ))
            ) : (
              <span className='text-muted'>-</span>
            )}
          </div>
        </div>
      ) : null}

      <div className='lpg-port-cell-block'>
        <span className='lpg-port-cell-label'>ALD/ADD</span>
        <span
          className={`lpg-port-cell-value${hasAldAdd ? '' : ' text-muted'}`}
        >
          {hasAldAdd ? aldAddText : '-'}
        </span>
      </div>
    </div>
  );
};

const ParcelIncotermTableLpg = ({
  tableModel,
  requiresCargoRatio = false,
  isAlphaEditable = false,
  procurementId = null,
  quantityDeliveryTermId = null,
  infoPrompt = null,
  onAlphaSaved,
}) => {
  const { rows = [], hasCargoRatioSelection } = tableModel ?? {};
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!rows.length) {
    return <p className='text-muted small mb-0'>No incoterm data available.</p>;
  }

  const handleStartEdit = row => {
    if (isSaving) return;
    setEditingRowId(row.id);
    setEditValue(
      row.alpha === 'Requested' || row.alpha === null || row.alpha === undefined
        ? ''
        : String(row.alpha)
    );
  };

  const handleCancelEdit = () => {
    if (isSaving) return;
    setEditingRowId(null);
    setEditValue('');
  };

  const handleSaveEdit = async row => {
    const alphaHps = parseAlphaInput(editValue);
    if (alphaHps === undefined) {
      notify.warning('Alpha must be a valid number.');
      return;
    }

    setIsSaving(true);
    try {
      const target = await resolveLpgAlphaTarget({
        row,
        procurementId,
        quantityDeliveryTermId,
      });
      if (target.alphaSource === 'detail') {
        await updateLpgDetailAlphaHps({
          recordId: target.recordId,
          alphaHps,
        });
      } else {
        await updateLpgAlphaHps({
          recordId: target.recordId,
          recordSnapshot: target.recordSnapshot,
          alphaHps,
        });
      }
      notify.success(MESSAGES.updated);
      setEditingRowId(null);
      setEditValue('');
      onAlphaSaved?.();
    } catch (error) {
      notify.apiError(error, 'update alpha HPS');
    } finally {
      setIsSaving(false);
    }
  };

  let incotermGroupIndex = -1;

  return (
    <div className='table-responsive lpg-incoterm-table-wrap'>
      <Table bordered className='lpg-incoterm-table mb-0'>
        <thead className='bg-light'>
          <tr>
            <th className='fw-semibold text-dark col-incoterm'>
              Incoterm - Route
            </th>
            <th className='fw-semibold text-dark col-port'>Port 1</th>
            <th className='fw-semibold text-dark col-port'>Port 2</th>
            <th className='fw-semibold text-dark col-port'>Port 3</th>
            {requiresCargoRatio ? (
              <th className='fw-semibold text-dark col-cargo-ratio'>
                Cargo Ratio
              </th>
            ) : null}
            <th className='fw-semibold text-dark col-parcel-size'>
              Parcel Size
            </th>
            <th className='fw-semibold text-dark col-information'>
              Information
            </th>
            <th className='fw-semibold text-dark col-alpha'>Alpha</th>
            {isAlphaEditable ? (
              <th className='fw-semibold text-dark text-center col-action'>
                Action
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            if (row.showIncotermCells) {
              incotermGroupIndex += 1;
            }

            const isGroupStart = row.showIncotermCells;
            const isOddGroup = incotermGroupIndex % 2 === 1;

            return (
              <tr
                key={row.id}
                className={[
                  isGroupStart ? 'lpg-incoterm-group-start' : '',
                  isOddGroup ? 'lpg-incoterm-group-odd' : '',
                  index % 2 === 1 ? 'lpg-row-alt' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {row.showIncotermCells ? (
                  <td
                    rowSpan={row.incotermRowSpan}
                    className='small text-dark align-middle lpg-incoterm-base-cell col-incoterm'
                  >
                    {displayValue(row.incotermRoute)}
                  </td>
                ) : null}

                {row.showIncotermCells ? (
                  <td
                    rowSpan={row.incotermRowSpan}
                    className='small text-dark align-top lpg-incoterm-base-cell col-port'
                  >
                    <PortCell
                      port={row.port1}
                      portNames={row.port1Names}
                      aldAdd={row.port1AldAdd}
                      parcelSizes={row.groupParcelSizes}
                      showParcelSizes
                    />
                  </td>
                ) : null}

                {row.showIncotermCells ? (
                  <td
                    rowSpan={row.incotermRowSpan}
                    className='small text-dark align-top lpg-incoterm-base-cell col-port'
                  >
                    <PortCell
                      port={row.port2}
                      portNames={row.port2Names}
                      aldAdd={row.port2AldAdd}
                      parcelSizes={row.port2 ? row.groupParcelSizes : []}
                      showParcelSizes={Boolean(row.port2)}
                    />
                  </td>
                ) : null}

                {row.showIncotermCells ? (
                  <td
                    rowSpan={row.incotermRowSpan}
                    className='small text-dark align-top lpg-incoterm-base-cell col-port'
                  >
                    <PortCell
                      port={row.port3}
                      portNames={row.port3Names}
                      aldAdd={row.port3AldAdd}
                      parcelSizes={row.port3 ? row.groupParcelSizes : []}
                      showParcelSizes={Boolean(row.port3)}
                    />
                  </td>
                ) : null}

                {requiresCargoRatio && row.showCargoRatioCell ? (
                  <td
                    rowSpan={row.cargoRatioRowSpan}
                    className='small align-middle col-cargo-ratio'
                  >
                    {hasCargoRatioSelection ? (
                      <span className='lpg-cargo-ratio-badge'>
                        {displayValue(row.cargoRatioLabel)}
                      </span>
                    ) : (
                      <CargoRatioEmptyState />
                    )}
                  </td>
                ) : null}

                <td className='small text-dark align-middle lpg-detail-cell lpg-parcel-size-cell col-parcel-size'>
                  {displayValue(row.parcelSize)}
                </td>
                <td className='small text-dark align-middle lpg-detail-cell lpg-information-cell col-information'>
                  {resolveInformationPrompt(infoPrompt)}
                </td>
                <td className='small align-middle lpg-detail-cell lpg-alpha-cell col-alpha'>
                  {editingRowId === row.id ? (
                    <Input
                      type='number'
                      step='any'
                      bsSize='sm'
                      style={{ minWidth: '70px' }}
                      value={editValue}
                      disabled={isSaving}
                      onChange={e => setEditValue(e.target.value)}
                      aria-label='Alpha'
                    />
                  ) : (
                    displayValue(row.alpha)
                  )}
                </td>
                {isAlphaEditable ? (
                  <td className='small align-middle text-center col-action lpg-action-cell'>
                    {editingRowId === row.id ? (
                      <div className='d-flex justify-content-center gap-1'>
                        <button
                          type='button'
                          className='btn btn-sm btn-soft-success'
                          aria-label='Save alpha'
                          disabled={isSaving}
                          onClick={() => handleSaveEdit(row)}
                        >
                          {isSaving ? (
                            <span
                              className='spinner-border spinner-border-sm'
                              role='status'
                              aria-hidden='true'
                            ></span>
                          ) : (
                            <i className='ri-check-line'></i>
                          )}
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm btn-soft-secondary'
                          aria-label='Cancel edit'
                          disabled={isSaving}
                          onClick={handleCancelEdit}
                        >
                          <i className='ri-close-line'></i>
                        </button>
                      </div>
                    ) : (
                      <button
                        type='button'
                        className='btn btn-sm btn-soft-primary'
                        aria-label='Edit alpha'
                        disabled={
                          isSaving ||
                          (Boolean(row.cargoRatioLabel) && !row.recordId)
                        }
                        onClick={() => handleStartEdit(row)}
                      >
                        <i className='ri-pencil-line'></i>
                      </button>
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default ParcelIncotermTableLpg;
